import { BaseService } from "./base.service";
import type { CreateDocumentDto } from "../types/document.dto";

export interface ShareDocumentDto {
  documentId: string;
  email: string;
}

class DocumentsService extends BaseService {
  private memberId: string | number;

  constructor(memberId: number | string) {
    super(`/members/${memberId}/documents`);
    this.memberId = memberId;
  }

  async getUncategorizedDocuments(dto: any) {
    // POST /members/:memberId/documents/uncategorized
    return this.post("/uncategorized", dto);
  }

  async getProcessingDocuments(dto: any) {
    // POST /members/:memberId/documents/processing
    return this.post("/processing", dto);
  }

  async bulkUpdateDocuments(ids: string[], update: object) {
    return this.put("/bulk-update", { ids, update });
  }

  async bulkDeleteDocuments(ids: string[]) {
    return this.post("/bulk-delete", { ids });
  }

  async getDocumentTypeCounts(options?: {
    mode?: "healthMemories" | "docsOnly" | "landing" | "dashboard";
  }) {
    // GET /members/:memberId/documents/document-type-counts?mode=...
    const suffix = options?.mode ? `?mode=${options.mode}` : "";
    return this.get(`/document-type-counts${suffix}`);
  }

  async getDocumentCount() {
    // GET /members/:memberId/documents/count
    return this.get("/count");
  }

  async getQuickAccessCounts(): Promise<{
    important: number;
    offline: number;
    private: number;
  }> {
    // GET /members/:memberId/documents/quick-access-counts
    return this.get("/quick-access-counts");
  }

  async vectorSearch(
    query: string,
    filters?: any,
    options?: { skipMemories?: boolean }
  ): Promise<any> {
    // POST /members/:memberId/documents/vector-search
    return this.post("/vector-search", {
      query,
      filters,
      skipMemories: options?.skipMemories,
    });
  }

  async fuzzySearch(searchTerm: string): Promise<any> {
    // POST /members/:memberId/documents/test-fuzzy-search
    return this.post("/fuzzy-search", { searchTerm });
  }

  async getById(id: string) {
    // GET /members/:memberId/documents/:id
    return this.get(`/${id}`);
  }

  async getByIds(ids: string[]) {
    // POST /members/:memberId/documents/fetch-by-ids
    return this.post("/fetch-by-ids", { ids });
  }

  async updateDocument(
    id: string,
    data: Partial<{
      tags: string[];
      notes: string;
      summary?: string | object;
      availableOffline?: boolean;
      isPrivate?: boolean;
      isImportant?: boolean;
      updateComment?: boolean;
    }>
  ) {
    return this.put(`/${id}`, data);
  }

  async deleteDocument(id: string) {
    return this.delete(`/${id}`);
  }

  async createDocument(data: CreateDocumentDto) {
    // POST to /members/:memberId/documents
    return this.post("", data);
  }

  async uploadDocument(file: File, source: string): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    return this.post(`/upload?source=${encodeURIComponent(source)}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async shareDocument(dto: ShareDocumentDto) {
    // POST to /members/:memberId/documents/share
    return this.post(`/share`, {
      documentId: dto.documentId,
      email: dto.email,
    });
  }

  async getAllDocumentsWithSettings(filters: any): Promise<any> {
    return this.post("/getAllWithSettings", filters);
  }

  /**
   * Fetch all documents for a member using POST to /members/:memberId/documents/getAll
   * Payload should include memberId (string) and filter/pagination params just like members.controller getAll.
   * Example payload: { member_id, filters, page, pageSize, sort }
   */
  /**
   * Fetch all documents for a member using POST to /members/:memberId/documents/getAll
   * member_id MUST be provided in the payload. No memberId extraction from endpoint.
   * Example payload: { member_id, filters, page, pageSize, sort }
   */
  async getAllDocuments(payload: Record<string, any> = {}) {
    if (!payload.member_id) {
      throw new Error(
        "member_id must be provided in the payload for getAllDocuments"
      );
    }
    return this.post("/getAll", payload);
  }

  /**
   * Download emergency card PDF for member using correct API base URL
   */
  async downloadEmergencyCardPdf(): Promise<Blob> {
    return this.get("/emergency-card", { responseType: "blob" });
  }

  async fetchEmergencyCardFileKey(): Promise<{ fileKey: string }> {
    return this.get("/emergency-card?meta=fileKey");
  }

  //Download wallet card PDF for member

  async downloadWalletCardPdf(): Promise<Blob> {
    return this.get(
      `/../../../emergency-info/${this.memberId}/wallet-card-pdf`,
      { responseType: "blob" }
    );
  }

  async hasDocuments(): Promise<{ hasDocuments: boolean }> {
    // GET /members/:memberId/documents/has-documents
    return this.get("/has-documents");
  }
}

export function documentService(memberId: string) {
  return new DocumentsService(memberId);
}
